import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Briefcase, MapPin, Clock } from 'lucide-react';

const JOBS_FEED_URL = 'https://paisleyscotland.com/api/jobs/feed?limit=8';
const JOBS_HUB_URL = 'https://paisleyscotland.com/jobs';
const FALLBACK_IMAGE = '/jobs-placeholder.jpg';

// Pure helper — kept outside the component so React's purity checks
// don't flag the use of Date.now() inside a render-time helper.
const formatAgo = (iso, now) => {
  if (!iso) return '';
  const days = Math.floor((now - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(days) || days < 0) return '';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return `${Math.floor(days / 30)} mo ago`;
};

/**
 * JobsStrip — homepage strip that mirrors the latest jobs from
 * paisleyscotland.com. Lives directly above "Explore the site".
 *
 * - Fetched live from paisleyscotland.com on every page load (no caching).
 * - Renders 0–8 cards depending on what comes back.
 * - If the feed is empty / errors / not yet live, still renders the strip
 *   with a "Browse all jobs on paisleyscotland.com" CTA so the section is
 *   never an awkward void.
 * - All cards link out to paisleyscotland.com in a new tab — paisley.org.uk
 *   does not host job detail pages of its own.
 */
const JobsStrip = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedLive, setFeedLive] = useState(true);
  // Capture "now" once on mount so render stays pure (avoids Date.now() in
  // render, which trips react-hooks/purity).
  const [now] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    fetch(JOBS_FEED_URL, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error('feed-down');
        return r.json();
      })
      .then((data) => {
        if (!alive) return;
        setJobs(Array.isArray(data) ? data : []);
        setFeedLive(true);
      })
      .catch(() => {
        if (!alive) return;
        setJobs([]);
        setFeedLive(false);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const ago = (iso) => formatAgo(iso, now);

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 py-24 text-white"
      data-testid="jobs-strip"
    >
      {/* Soft aurora glow to match site language */}
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-300 mb-3 inline-flex items-center gap-2">
              <Briefcase size={13} /> Local jobs
            </p>
            <h2 className="text-5xl md:text-6xl font-bold tracking-[-0.04em] leading-none">
              Jobs in{' '}
              <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-300 via-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">
                Paisley
              </span>
            </h2>
            <p className="mt-3 text-sm text-slate-300 max-w-xl">
              Live vacancies across Paisley, Renfrewshire &amp; Glasgow — pulled straight from
              <a
                href={JOBS_HUB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-300 hover:text-sky-200 underline-offset-4 hover:underline mx-1.5"
              >
                paisleyscotland.com
              </a>
              the moment they{'\u2019'}re posted.
            </p>
          </div>
          <a
            href={JOBS_HUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-sky-50 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
            data-testid="jobs-strip-cta"
          >
            See all jobs <ArrowUpRight size={15} />
          </a>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 rounded-3xl bg-white/5 ring-1 ring-white/10 animate-pulse" />
            ))}
          </div>
        ) : jobs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="jobs-strip-grid">
            {jobs.slice(0, 8).map((job) => {
              const img = job.image_url && job.image_url.trim() ? job.image_url : FALLBACK_IMAGE;
              return (
                <a
                  key={job.id || job.slug || job.url}
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-slate-900/60 hover:ring-sky-300/40 hover:bg-slate-900/80 transition-all"
                  data-testid={`jobs-strip-card-${job.slug || job.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-800">
                    <img
                      src={img}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        if (!e.target.dataset.f) {
                          e.target.dataset.f = '1';
                          e.target.src = FALLBACK_IMAGE;
                        }
                      }}
                    />
                  </div>
                  <div className="p-4">
                    {job.job_type && (
                      <span className="inline-block text-[10px] uppercase font-bold tracking-widest text-sky-300 bg-sky-500/10 px-2 py-0.5 rounded mb-2">
                        {job.job_type}
                      </span>
                    )}
                    <h3 className="text-base font-bold leading-snug line-clamp-2 group-hover:text-sky-200 transition-colors">
                      {job.title}
                    </h3>
                    {job.company && (
                      <p className="text-xs text-slate-300 mt-1 line-clamp-1">{job.company}</p>
                    )}
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-400">
                      {job.location && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <MapPin size={11} className="shrink-0" />
                          <span className="truncate">{job.location}</span>
                        </span>
                      )}
                      {ago(job.posted_at) && (
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <Clock size={11} /> {ago(job.posted_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        ) : (
          /* Empty / feed-not-live state — keeps the section purposeful */
          <div
            className="rounded-3xl ring-1 ring-white/10 bg-slate-900/60 p-10 text-center"
            data-testid="jobs-strip-empty"
          >
            <Briefcase size={32} className="mx-auto text-sky-300 mb-4" />
            <p className="text-lg font-semibold mb-1">
              {feedLive ? 'No new vacancies posted right now' : 'We\u2019re syncing with paisleyscotland.com'}
            </p>
            <p className="text-sm text-slate-300 max-w-md mx-auto mb-6">
              Browse the full board of local jobs — across Paisley, Renfrewshire &amp; Glasgow — on our
              sister site.
            </p>
            <a
              href={JOBS_HUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded-full px-5 py-2.5 text-sm font-bold transition-colors"
              data-testid="jobs-strip-empty-cta"
            >
              Browse all jobs <ArrowUpRight size={15} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default JobsStrip;
